// App-wide reference data (document types, departments, user directory) cached in a context.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from './api';
import type { Department, DocType, UserLookup } from './types';
import { useAuth } from './store';

interface Catalog { types: DocType[]; departments: Department[]; users: UserLookup[]; loaded: boolean; reload: () => Promise<void>; typeFull: (id: number) => Promise<DocType> }
const Ctx = createContext<Catalog>(null as any);
export const useCatalog = () => useContext(Ctx);

export function CatalogProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [types, setTypes] = useState<DocType[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<UserLookup[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [cache, setCache] = useState<Record<number, DocType>>({});
  const reload = useCallback(async () => {
    if (!user) return;
    const [t, d, u] = await Promise.all([api.get<DocType[]>('/api/catalog/types'), api.get<Department[]>('/api/auth/departments'), api.get<UserLookup[]>('/api/auth/users/lookup')]);
    setTypes(t); setDepartments(d); setUsers(u); setCache({}); setLoaded(true);
  }, [user?.id]);
  useEffect(() => { reload().catch(() => setLoaded(true)); }, [reload]);
  const typeFull = async (id: number) => {
    if (cache[id]) return cache[id];
    const t = await api.get<DocType>('/api/catalog/types/' + id);
    setCache(c => ({ ...c, [id]: t }));
    return t;
  };
  return <Ctx.Provider value={{ types, departments, users, loaded, reload, typeFull }}>{children}</Ctx.Provider>;
}

const fold = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
export const specificSection = (name: string) => 'Date specifice — ' + name;
/**
 * v1.17 — a register with document types attached: its "Tipul documentului" field offers those types (each with its answer term,
 * the old list values stay valid), and the chosen type's own fields follow in a section of their own. Mirrors the server's rule.
 */
export function withDocType(reg: DocType, sub: DocType | null, opts?: { strict?: boolean; keep?: any[] }): DocType {
  const dts = reg.docTypes || [];
  // registering in a register with no document types: "Tipul documentului" offers nothing (nothing can be registered yet)
  if (!dts.length) return opts?.strict && reg.isRegistry ? { ...reg, attributes: (reg.attributes || []).map(a => a.docTypeField ? { ...a, items: [], listAllowAdd: false } : a) } : reg;
  const names = new Set(dts.map(d => fold(d.name)));
  const codes = new Set((reg.attributes || []).map(a => a.code));
  const attrs = (reg.attributes || []).map(a => !a.docTypeField ? a : {
    ...a, listAllowAdd: false,
    // strict (registering): only the register's types; otherwise the list's former values stay readable / valid too
    items: [...dts.map(d => ({ id: -d.id, listId: a.listId || 0, value: d.name, label: d.name, deadlineDays: d.deadlineDays ?? null, active: true } as any)),
      ...(a.items || []).filter(i => !names.has(fold(i.value)) && (!opts?.strict || (opts.keep || []).some(k => k != null && fold(String(k)) === fold(i.value))))],
  });
  const extra = sub ? (sub.attributes || []).filter(a => !codes.has(a.code)).map(a => ({ ...a, section: specificSection(sub.name), docTypeSpecific: true })) : [];
  return { ...reg, attributes: [...attrs, ...extra], fileRequired: reg.fileRequired || !!sub?.fileRequired };
}
/** The type among the register's types whose name is the value of its "Tipul documentului" field. */
export function docTypeByName(reg: DocType, value: any): DocType | null {
  if (value == null || value === '') return null;
  return (reg.docTypes || []).find(d => fold(d.name) === fold(String(value))) || null;
}
